'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/components/storefront/ui/button';
import { SpinnerGap } from '@phosphor-icons/react';

// Providers we ship today. Add additional providers here once they're
// configured in Supabase + the corresponding cloud console. Keeping
// the union narrow stops a misconfigured button from rendering and
// erroring out.
type Provider = 'google';

type Props = {
  /** Where to land after OAuth completes — passed through to the callback. */
  next?: string | null;
};

export function OAuthButtons({ next }: Props) {
  const t = useTranslations('auth');
  const locale = useLocale();
  const [pending, setPending] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleOAuth(provider: Provider) {
    setError(null);
    setPending(provider);

    // Go to OUR OWN origin first, not straight to the auth domain. That hop
    // sets the single-use nonce cookie that binds the handoff to this browser
    // — a cookie the auth domain cannot set for us — and then forwards to
    // auth.salmasoft.com/oauth/start. Without it the token in the eventual
    // /auth/finish URL is redeemable by anyone who sees it.
    const params = new URLSearchParams({ provider, locale });
    if (next) params.set('next', next);

    // Full-page navigation, not fetch — the browser must follow the redirect
    // chain so both the nonce cookie here and the PKCE cookie on the auth
    // domain are set.
    window.location.href = `/auth/oauth-start?${params.toString()}`;
    // Leave `pending` set; the navigation kills this React tree.
  }

  return (
    <div className="space-y-2.5">
      <Button
        type="button"
        variant="outline"
        color="default"
        size="lg"
        fullWidth
        onClick={() => handleOAuth('google')}
        disabled={pending !== null}
        leftIcon={pending === 'google' ? <SpinnerGap className="animate-spin" /> : <GoogleIcon />}
      >
        {pending === 'google' ? t('oauthRedirecting') : t('continueWithGoogle')}
      </Button>

      {error && <p className="text-sm text-danger" role="alert">{error}</p>}
    </div>
  );
}

// ── Inline brand marks ────────────────────────────────────────────
// Kept inline (not Phosphor) so they render the canonical multicolor
// Google "G" per the brand guidelines.

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.28 6.61l3.99 3.1C6.22 6.86 8.87 4.75 12 4.75z"
      />
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.28 1.4-1.07 2.59-2.28 3.39v2.83h3.69c2.16-1.99 3.61-4.93 3.61-8.46z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.29c-.21-.65-.34-1.34-.34-2.04s.13-1.39.34-2.04l-3.99-3.1C.46 8.93 0 10.42 0 12c0 1.58.46 3.07 1.28 4.39l3.99-3.1z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.07 7.94-2.9l-3.69-2.83c-1.02.69-2.34 1.1-4.25 1.1-3.13 0-5.78-2.11-6.73-4.96l-3.99 3.1C3.26 21.31 7.31 24 12 24z"
      />
    </svg>
  );
}

